import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';


// Material UI
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import SendIcon from '@material-ui/icons/Send';

// Data
import { isValidEmail } from '../../../lib/helpers/validation.helpers';
import messages from './invite-form.messages';

// Components
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormHelperText, FormInput } from '../../_core/_ui/forms.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { ROLES } from '../../../../_configuration';
import { Label } from '../../_core/_ui/typography.component';
import { getActiveAssessmentId } from '../../../lib/services/assessment.service';
import { useMemberColumnListOptions } from '../../Member/MemberList/member-list.utils';
import { IRow, SimpleTable } from '../../_core/_ui/simple-table.component';
import { HeaderWithTooltip } from '../../_core/_ui/header-with-tooltip.component';
import { getCurrMember } from '../../../lib/services/member.service';

interface InviteFormProps {
  ageGroups: IAgeGroup[];
  canInviteToOrganization: boolean;
  sendInvite: (email: string, phone: string, memberRoles: IMemberRoles[]) => Promise<any>;
  showSaved: boolean;
  submitError: string | null;
}

const InviteForm: React.FC<InviteFormProps> = ({
  ageGroups,
  canInviteToOrganization,
  sendInvite,
  showSaved,
  submitError
}) => {
  const assessmentId = getActiveAssessmentId();
  const { member: currMember } = getCurrMember();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // Seed permissions per invite template; the inviter can still tick any box after.
  // COMMUNICATION is deliberate on Super User, Age Director and Lead Evaluator only.
  const ROLE_DEFINITIONS = {
    'Super User': {
      superUser: ['CONFIGURATION', 'USER_MANAGEMENT', 'CONFIG_DRILLS', 'COMMUNICATION'],
      assessment: ['CONFIGURATION', 'SCORING', 'CHECK_IN', 'COMMUNICATION'],
      ageGroups: ['CONFIGURATION', 'USER_MANAGEMENT', 'PLAYER_NAMES', 'REPORTS', 'COMMUNICATION']
    },
    'Age Director': {
      superUser: [],
      assessment: ['SCORING', 'CHECK_IN', 'COMMUNICATION'],
      ageGroups: ['USER_MANAGEMENT', 'CONFIGURATION', 'PLAYER_NAMES', 'REPORTS', 'COMMUNICATION']
    },
    'Lead Evaluator': {
      superUser: [],
      assessment: ['SCORING', 'CHECK_IN', 'COMMUNICATION'],
      ageGroups: ['CONFIGURATION', 'REPORTS', 'COMMUNICATION']
    },
    'Evaluator': {
      superUser: [],
      assessment: ['SCORING'],
      ageGroups: []
    },
    'Check-in Desk': {
      superUser: [],
      assessment: ['CHECK_IN'],
      ageGroups: []
    }
  };

  const roleOptions = Object.keys(ROLE_DEFINITIONS).map(role => ({
    value: role,
    label: role
  }));

  // Unified permissions state
  const [superUserRoles, setSuperUserRoles] = useState<string[]>([]);
  const [assessmentRoles, setAssessmentRoles] = useState<string[]>([]);
  const [genericAgeGroupRoles, setGenericAgeGroupRoles] = useState<string[]>([]);

  // Track which role is selected
  const [selectedRole, setSelectedRole] = useState<string>('Evaluator');
  // Apply role permissions when changed
  useEffect(() => {
    const roleDef = ROLE_DEFINITIONS[selectedRole as keyof typeof ROLE_DEFINITIONS];
    if (roleDef) {
      setSuperUserRoles(roleDef.superUser);
      setAssessmentRoles(roleDef.assessment);
      setGenericAgeGroupRoles(roleDef.ageGroups);
    }
  }, [selectedRole]);

  // Helper: toggle a role in a list
  const toggleRole = (roles: string[], role: string) =>
    roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role];

  // Unified columns organized by category
  const { superUserOptions, assessmentOptions, ageGroupOptions } = useMemberColumnListOptions();
  // Remove duplicate columns (keep unique by key)
  const filterOutMetaCols = (c: any) => c.key !== 'name' && c.key !== 'all' && c.key !== 'view';
  const filterOutMetaColsArr = (c: any) => !['name', 'all', 'view'].includes(c.key);
  // Type guard for MemberListOption
  function isMemberListOption(obj: any): obj is import('../../Member/MemberList/member-list.types').MemberListOption {
    return obj && typeof obj.key === 'string';
  }
  // Helper to ensure ag._id is a string
  function safeId(id: any): string {
    return typeof id === 'string' ? id : '';
  }

  // Organize columns by category
  const associationColumns = superUserOptions.filter(filterOutMetaCols).filter(isMemberListOption);
  const assessmentColumns = assessmentOptions.filter(filterOutMetaColsArr).filter(isMemberListOption);
  const ageGroupColumns = ageGroupOptions.filter(filterOutMetaColsArr).filter(isMemberListOption);

  // What the invite would grant — the send payload and the send gate read the same list, so an
  // invite that grants nothing can't be sent. The server refuses one too.
  const memberRoles: IMemberRoles[] = useMemo(() => [
    { roles: superUserRoles },
    { assessmentId, roles: assessmentRoles },
    ...ageGroups
      .map(ag => ({
        ageGroupId: safeId(ag._id),
        roles: genericAgeGroupRoles
      }))
      .filter(mr => mr.ageGroupId && (canInviteToOrganization || currMember.isAdmin || currMember.rolesByAgeGroup.some(r => r.ageGroupId === mr.ageGroupId && r.roles.includes(ROLES.USER_MANAGEMENT))))
  ].filter(mr => mr.roles.length > 0), [superUserRoles, assessmentId, assessmentRoles, ageGroups, genericAgeGroupRoles, canInviteToOrganization, currMember]);

  // Send handler
  const handleSendInvite = useCallback(() => {
    if (name && email && isValidEmail(email) && memberRoles.length) {
      sendInvite(email, phone || '', memberRoles); // Send empty string if phone is empty
      setName('');
      setEmail('');
      setPhone('');
      setSuperUserRoles([]);
      setAssessmentRoles([]);
      setGenericAgeGroupRoles([]);
    }
  }, [name, email, phone, memberRoles, sendInvite]);

  // Flipped layout: scopes (Association/Assessment/Age Group) become rows, and the
  // union of permission columns becomes the headers. A cell is empty when the
  // scope has no applicable setting for that role.
  const scopeRows = useMemo(() => {
    const list: Array<{
      label: string;
      columns: typeof associationColumns;
      selectedRoles: string[];
      setRoles: React.Dispatch<React.SetStateAction<string[]>>;
    }> = [];
    if (canInviteToOrganization) {
      list.push({ label: 'Association', columns: associationColumns, selectedRoles: superUserRoles, setRoles: setSuperUserRoles });
      list.push({ label: 'Assessment', columns: assessmentColumns, selectedRoles: assessmentRoles, setRoles: setAssessmentRoles });
    }
    list.push({ label: 'Age Group', columns: ageGroupColumns, selectedRoles: genericAgeGroupRoles, setRoles: setGenericAgeGroupRoles });
    return list;
  }, [canInviteToOrganization, associationColumns, assessmentColumns, ageGroupColumns, superUserRoles, assessmentRoles, genericAgeGroupRoles]);

  const unifiedColumns = useMemo(() => {
    const sources = canInviteToOrganization
      ? [associationColumns, assessmentColumns, ageGroupColumns]
      : [ageGroupColumns];
    const seen = new Map<string, { label: string; role?: string }>();
    sources.forEach(source => source.forEach(col => {
      if (col.role && !seen.has(col.role)) seen.set(col.role, col);
    }));
    return Array.from(seen.values());
  }, [canInviteToOrganization, associationColumns, assessmentColumns, ageGroupColumns]);

  const headerRows: IRow[] = useMemo(() => [{
    cells: [
      { content: 'Scope', borderRight: true },
      ...unifiedColumns.map(col => ({
        content: <HeaderWithTooltip label={col.label} role={col.role} />,
      })),
    ],
  }], [unifiedColumns]);

  const rows: IRow[] = useMemo(() => scopeRows.map(scope => {
    const scopeRoleSet = new Set(scope.columns.map(c => c.role).filter((r): r is string => !!r));
    return {
      cells: [
        { content: scope.label, borderRight: true },
        ...unifiedColumns.map(col => ({
          content: col.role && scopeRoleSet.has(col.role)
            ? <Checkbox
                checked={scope.selectedRoles.includes(col.role)}
                onChange={() => scope.setRoles(r => toggleRole(r, col.role!))}
              />
            : null,
        })),
      ],
    };
  }), [scopeRows, unifiedColumns]);

  return (
    <Box>
      <Box flexGrow={1}>
        <Box mb={2}><Label>Name</Label><FormInput fullWidth value={name} onChange={e => setName(e.target.value)} placeholder="Full name" /></Box>
        <Box mb={2}><Label>Email</Label><FormInput fullWidth value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" type="email" autoComplete="off" /></Box>
        <Box mb={2}><Label>Phone <span style={{ color: '#999', fontSize: '0.9em' }}>(optional)</span></Label><FormInput fullWidth value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number (optional)" type="tel" autoComplete="off" /></Box>
        {canInviteToOrganization && <Box mb={2}>
          <Label>Role Template</Label>
          <Select
            value={roleOptions.find(option => option.value === selectedRole)}
            onChange={(selectedOption) => setSelectedRole(selectedOption?.value || 'Evaluator')}
            options={roleOptions}
            placeholder="Select a role template"
            isClearable={false}
          />
        </Box>}
        <Box mb={2}><Label>Permissions</Label></Box>
        <SimpleTable headerRows={headerRows} rows={rows} stickyHeader stickyFirstColumn columnMinWidths />
      </Box>
      <Box mt={2} display="flex" flexDirection="row" alignItems="center">
        <PrimaryButton
          disabled={!(name && email && isValidEmail(email) && memberRoles.length)}
          onClick={handleSendInvite}
          endIcon={<SendIcon />}
        >
          <IntlMsg {...messages.btnSendInvite} />
        </PrimaryButton>
        {!memberRoles.length && <Box ml={2}><FormHelperText>Choose at least one permission to send an invite</FormHelperText></Box>}
        {submitError && <Box ml={2}><FormHelperText error>{submitError}</FormHelperText></Box>}
        {showSaved && <Box ml={2}><FormHelperText className="success">Invite sent</FormHelperText></Box>}
      </Box>
    </Box>
  );
};

InviteForm.propTypes = {
  sendInvite: PropTypes.func.isRequired,
};

export default InviteForm;
